// The fleet radio's wire (WalkieTalkie comms, docs: "WalkieTalkie Comms in The Chair — Handoff"). The Chair is the fourth
// client on Grey's existing radio network, alongside the Windows radio, the browser radio (walkie.js, the reference port) and
// OpenChannel's module. Everything in this file is an INVARIANT shared with those clients: channel names, channel ids, the
// 20-byte voice packet and the private-call encryption must match them byte for byte, or players end up on a parallel
// network that looks connected and carries nobody's voice. Change nothing here on one side only.
//
// No DOM and no Node: WebCrypto (crypto.subtle) and btoa/atob exist in browsers and in Node 22, so the unit test runs it as is.

export const RADIO_MARKER = 0xc2;              // first byte of every voice packet
export const RADIO_TYPE_VOICE = 3;             // second byte: a voice frame
export const RADIO_HDR = 20;                   // header bytes before the Opus payload
export const RADIO_FRAME_MS = 40;              // Opus, mono, 40 ms frames
export const RADIO_BITRATE = 24000;            // what the browser radio encodes at
export const RADIO_STATE_MS = 5000;            // the STATE lease: the whole tuning re-declared this often (the server drops a silent client at 20 s)
export const RADIO_FLAG_ENCRYPTED = 1;         // flags bit 0: the payload is AES-GCM (private calls)
// The version this client reports. Bump it with every radio change: it's how logs tell builds apart (a hardcoded version
// string once hid which build every bug came from for a year).
export const RADIO_CLIENT_VERSION = "TheChair-radio/0.2.0";

// The fleet's frequencies, as the browser radio lists them (walkie.js CHANNELS).
export const FLEET_CHANNELS: { name: string; mhz: number }[] = [
  { name: "Fleet-Net", mhz: 50.000 },
  { name: "Operations", mhz: 51.000 },
  { name: "Security", mhz: 54.000 },
  { name: "Air Tactical", mhz: 60.000 },
  { name: "Trucker Radio", mhz: 27.185 },
  { name: "Emergency", mhz: 200.000 },
];
// The ship's own channel: a group channel, so it can never collide with a fleet frequency. A desktop operator joins it by
// typing the group name. (One constant: if Grey would rather the ship sat on a frequency, change it here.)
export const SHIP_RADIO = { name: "The Stewart", group: "Event Horizon" };   // (the ship renamed, Grey 2026-11-01: the group keeps its old name so desktop radios on it stay on it)
// The departments' group channels (GROUP:<department>), one per department of the ship (shared/skills.ts DEPT_ORDER)
export const DEPT_RADIO: [string, string][] = [["command", "Command"], ["flight", "Flight"], ["operations", "Operations"], ["tactical", "Tactical"], ["security", "Security"], ["engineering", "Engineering"], ["science", "Science"], ["medical", "Medical"], ["counsel", "Counsel"], ["supply", "Supply"], ["crew", "Lower Decks"]];

// ---------- channel identity ----------
// FREQ:<hz> (integer hertz), GROUP:<name> (trimmed, lower-cased), DIRECT:<a>|<b> (both names normalised, then sorted)
export const freqCanonical = (mhz: number) => "FREQ:" + Math.round(mhz * 1e6);
export const groupCanonical = (name: string) => "GROUP:" + String(name ?? "").trim().toLowerCase();
// trailing fleet glyphs stripped, trimmed, lower-cased: one identity per person everywhere (walkie.js normUser)
export const FLEET_GLYPHS = /[♇⎈⚑]+$/;
export function normUser(n: unknown): string { return String(n || "").replace(FLEET_GLYPHS, "").trim().toLowerCase(); }
export function directCanonical(a: string, b: string): string { const p = [normUser(a), normUser(b)].sort(); return "DIRECT:" + p[0] + "|" + p[1]; }
export const directMembers = (canon: string): string[] => canon.startsWith("DIRECT:") ? canon.slice(7).split("|") : [];
// a channel's id: the first four bytes of the SHA-256 of its canonical string, big-endian
export async function channelId(canon: string): Promise<number> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canon));
  const b = new Uint8Array(buf);
  return ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
}
export const chHex = (id: number) => (id >>> 0).toString(16).toUpperCase().padStart(8, "0");

// ---------- the voice packet ----------
// [0] marker 0xC2, [1] type 3, [2..5] token u32, [6] epoch u8, [7..10] channel u32, [11..12] txId u16, [13..14] seq u16,
// [15..18] timestamp u32 (ms since the transmission began), [19] flags u8, then the Opus payload. Little-endian.
export interface VoicePacket { token: number; epoch: number; channel: number; txId: number; seq: number; timestamp: number; flags: number; payload: Uint8Array }
export function encodePacket(p: VoicePacket): Uint8Array {
  const pkt = new Uint8Array(RADIO_HDR + p.payload.length), dv = new DataView(pkt.buffer);
  pkt[0] = RADIO_MARKER; pkt[1] = RADIO_TYPE_VOICE;
  dv.setUint32(2, p.token >>> 0, true); pkt[6] = p.epoch & 0xff;
  dv.setUint32(7, p.channel >>> 0, true);
  dv.setUint16(11, p.txId & 0xffff, true); dv.setUint16(13, p.seq & 0xffff, true);
  dv.setUint32(15, p.timestamp >>> 0, true); pkt[19] = p.flags & 0xff;
  pkt.set(p.payload, RADIO_HDR);
  return pkt;
}
export function decodePacket(pkt: Uint8Array): VoicePacket | null {
  if (pkt.length < RADIO_HDR || pkt[0] !== RADIO_MARKER || pkt[1] !== RADIO_TYPE_VOICE) return null;
  const dv = new DataView(pkt.buffer, pkt.byteOffset, pkt.byteLength);
  return { token: dv.getUint32(2, true), epoch: pkt[6], channel: dv.getUint32(7, true), txId: dv.getUint16(11, true), seq: dv.getUint16(13, true), timestamp: dv.getUint32(15, true), flags: pkt[19], payload: pkt.slice(RADIO_HDR) };
}
export function b64(u8: Uint8Array): string { let s = ""; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return btoa(s); }
export function unb64(str: string): Uint8Array { const s = atob(str); const u = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i); return u; }

// ---------- private-call encryption (desktop radios encrypt DM audio; walkie.js deriveDirectKey) ----------
// key = PBKDF2-SHA256("wt-dm-" + canonical, salt = channelId (LE, 4 bytes) + "WalkieTalkie", 100000 iterations, 32 bytes)
// payload = nonce (12: channelId LE u32, seq LE u16, 6 random) + AES-GCM ciphertext + tag (16)
export const DM_ITERATIONS = 100000;
export function directSalt(chId: number): Uint8Array {
  const salt = new Uint8Array(16); new DataView(salt.buffer).setUint32(0, chId >>> 0, true);
  salt.set(new TextEncoder().encode("WalkieTalkie"), 4);
  return salt;
}
export async function deriveDirectKey(canon: string, chId: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode("wt-dm-" + canon), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: directSalt(chId) as BufferSource, iterations: DM_ITERATIONS }, base, 256);
  return crypto.subtle.importKey("raw", bits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}
export function directNonce(chan: number, seqNo: number, random?: Uint8Array): Uint8Array<ArrayBuffer> {
  const nonce = new Uint8Array(12), nv = new DataView(nonce.buffer);
  nv.setUint32(0, chan >>> 0, true); nv.setUint16(4, seqNo & 0xffff, true);
  if (random) nonce.set(random.subarray(0, 6), 6); else crypto.getRandomValues(nonce.subarray(6));
  return nonce;
}
export async function encryptDirect(key: CryptoKey, opus: Uint8Array, chan: number, seqNo: number, random?: Uint8Array): Promise<Uint8Array> {
  const nonce = directNonce(chan, seqNo, random);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce, tagLength: 128 }, key, opus as BufferSource));
  const out = new Uint8Array(12 + ct.length); out.set(nonce, 0); out.set(ct, 12);
  return out;
}
export async function decryptDirect(key: CryptoKey | null, payload: Uint8Array): Promise<Uint8Array | null> {
  if (!key || payload.length < 12 + 16 + 1) return null;
  try {
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: payload.slice(0, 12), tagLength: 128 }, key, payload.slice(12));
    return new Uint8Array(pt);
  } catch { return null; }
}

// ---------- The Chair's relay (client <-> game server <-> the gateway) ----------
// The browser can't reliably open a WSS to the gateway's self-signed certificate on another origin, so the game server holds
// one outbound connection per signed-in player (server/radio.ts) and relays the radio's JSON both ways, untouched.
export type WtFrame = { type: string; [k: string]: unknown };
export type RadioLink = "off" | "connecting" | "on" | "retrying" | "failed";
// custody (what we sent) and receipts (what we heard and played): appended to logs/radio.jsonl by the game server. No audio,
// no passwords: counts only. Pair a sender's TX end with each listener's RX receipt to split "the network lost it" from "the
// client threw it away".
export type RadioLogRec =
  | { kind: "tx"; tx: number; ch: string; canon: string; pkts: number; ms: number; enc: boolean }
  | { kind: "rx"; who: string; tx: number; ch: string; canon: string; received: number; decoded: number; played: number; dropped: number; enc: boolean }
  | { kind: "note"; text: string };
export type RadioClientMsg =
  | { t: "radio"; op: "join" }                                          // on the air by your game login: the server says under what name, a browser never does
  | { t: "radio"; op: "logout" }
  | { t: "radio"; op: "wt"; m: WtFrame }                                 // a radio frame for the gateway (STATE, PTT_DOWN, PTT_UP, VOICE_TCP)
  | { t: "radio"; op: "log"; rec: RadioLogRec; line: string };          // a custody or receipt line, for logs/radio.jsonl
export type RadioServerMsg =
  | { t: "radio"; op: "status"; link: RadioLink; user?: string; why?: string }
  | { t: "radio"; op: "wt"; m: WtFrame };                                // a radio frame from the gateway (WELCOME, CHANNEL_UPDATE, TX_START, VOICE_TCP, ...)
